import type { ReactNode } from 'react'
import { View } from 'react-native'

import { ShareModal, type TShareModalProps } from '@/src/features/share/ui/components/ShareModal'
import { TEST_ID } from '@/src/general/constants/testId'
import { UTM } from '@/src/general/constants/utm'
import { useUrlBuilder } from '@/src/general/hooks/useUrlBuilder'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TMediumCommunityApi } from '../../api/communityApi.types'
import { EVENT_ORIGIN } from '../../models/community.constants'
import { CommunityThumbnail } from './CommunityThumbnail'

export type TCommunityShareModalProps = Pick<TShareModalProps, 'trackingSource'> &
  Pick<TMediumCommunityApi, 'id' | 'lifetime' | 'name' | 'picture' | 'slug'> &
  Pick<TShareModalProps, 'children'>

export const CommunityShareModal = ({
  id,
  name,
  lifetime,
  picture,
  slug,
  trackingSource,
  children,
}: TCommunityShareModalProps): ReactNode => {
  const { t } = useTranslate()
  const { routes } = useRoutes()

  const urlBuilder = useUrlBuilder()
  const url = urlBuilder.withUTM({
    route: routes.communityLanding({
      id,
      slug,
      course: 'direct',
      origin: EVENT_ORIGIN.COMMUNITY_SHARE,
      source: TRACKING_SOURCE.SHARE_LINK,
    }),
    campaign: UTM.CAMPAIGN.COMMUNITY(id),
  })

  const getSharedData = async () => {
    const text = t('share.share-by-email-body', {
      communityName: name,
      lifetime,
      url,
    })
    return { text, title: name, url }
  }

  return (
    <ShareModal
      key="community-share-modal"
      getShareData={getSharedData}
      title={t('publication.prayed.share.community.modal.title')}
      description={
        <View className="py-4">
          <CommunityThumbnail
            title={name}
            titleProps={{ bold: true, size: 'md' }}
            imgSource={getImageUrl(picture)}
            imageTestID={TEST_ID.PUBLICATION.REWARD.SHARE_CARD.COMMUNITY_IMG}
            showImage
            ellipsis={false}
          />
        </View>
      }
      trackingSource={trackingSource}
    >
      {children}
    </ShareModal>
  )
}
